import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { obtenerVigilantesPorServicio } from '../data/FirestoreCuadrantes';
import { fetchVigilantesPorServicio } from '../../admin/firestoreService';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase';

// Función helper para formatear el nombre del vigilante
const formatearVigilante = (vigilante) => {
  const nombreCompleto = `${vigilante.nombre || ''} ${vigilante.apellidos || ''}`.trim();
  const persona = vigilante.persona || '';
  
  if (persona && nombreCompleto) {
    return `${persona} - ${nombreCompleto}`;
  } else if (persona) {
    return persona;
  } else if (nombreCompleto) {
    return nombreCompleto;
  } else {
    return vigilante.id || 'Sin nombre';
  }
};

// Función auxiliar para obtener la delegación del código postal
const obtenerDelegacionDeCodigoPostal = (codigoPostal) => {
  if (!codigoPostal || codigoPostal.length < 2) {
    return null;
  }
  // Las dos primeras cifras del código postal indican la delegación
  return codigoPostal.substring(0, 2);
};

const AsignarVigilante = ({ 
  open, 
  onClose, 
  turnoId, 
  cuadranteId, 
  currentService, 
  servicioId, 
  onVigilanteAsignado,
  onAsignarAFreematica,
  onAsignacionPendiente, // Nueva prop para agregar a cambios pendientes
  empresaCuadrante, // Nueva prop: empresa del cuadrante
  delegacionCuadrante // Nueva prop: delegación del cuadrante
}) => {
  const [vigilantes, setVigilantes] = useState([]);
  const [vigilantesFiltrados, setVigilantesFiltrados] = useState([]);
  const [busquedaVigilante, setBusquedaVigilante] = useState('');
  const [vigilanteSeleccionado, setVigilanteSeleccionado] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [asignandoAFreematica, setAsignandoAFreematica] = useState(false);
  const [vigilantesExternos, setVigilantesExternos] = useState([]);
  
  // Nuevos estados para filtros
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroDelegacion, setFiltroDelegacion] = useState('');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [soloVigilantesCompletos, setSoloVigilantesCompletos] = useState(false);

  // Mostrar panel de filtros al abrir (no aplicar por defecto para que la búsqueda por nombre/empleado funcione)
  useEffect(() => {
    if (open) {
      setMostrarFiltros(true);
      // No pre-rellenar empresa/delegación para evitar lista vacía al abrir desde un turno
    }
  }, [open]);

  // Cargar vigilantes del servicio
  useEffect(() => {
    const cargarVigilantes = async () => {
      if (!open || !currentService) return;

      try {
        setLoading(true);
        setError('');

        // Obtener TODOS los vigilantes del servicio (no solo los del servicio específico)
        let vigilantesData = await fetchVigilantesPorServicio(currentService);

        if (vigilantesData && vigilantesData.length > 0) {
          // Validar, formatear y enriquecer vigilantes con delegación del código postal
          const vigilantesValidados = vigilantesData.map(v => {
            // Obtener delegación del código postal
            let delegacionVigilante = null;
            if (v.direccion && v.direccion.codigoPostal) {
              delegacionVigilante = obtenerDelegacionDeCodigoPostal(v.direccion.codigoPostal);
            }
            
            // Enriquecer datos del vigilante
            const vigilanteEnriquecido = {
              ...v,
              delegacionCalculada: delegacionVigilante,
              empresa: v.empresa || '0001' // Empresa por defecto si no está definida
            };
            
            // Si no tiene persona, usar nombre completo
            if (!vigilanteEnriquecido.persona && (v.nombre || v.apellidos)) {
              vigilanteEnriquecido.persona = `${v.nombre || ''} ${v.apellidos || ''}`.trim();
            }
            
            return vigilanteEnriquecido;
          });

          setVigilantes(vigilantesValidados);
          setVigilantesFiltrados(vigilantesValidados);
          setVigilantesExternos([]); // No necesitamos usuarios externos ya que cargamos todos
        } else {
          setError('No se encontraron usuarios disponibles');
        }
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        setError('Error al cargar usuarios: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    cargarVigilantes();
  }, [open, currentService]);

  // Filtrar vigilantes cuando cambie la búsqueda o los filtros
  useEffect(() => {
    let filtrados = [...vigilantes];
    
    // Filtro: Solo vigilantes con datos completos
    if (soloVigilantesCompletos) {
      filtrados = filtrados.filter(v => v.empresa && v.empresa !== '' && v.delegacionCalculada);
    }
    
    // Filtro por empresa
    if (filtroEmpresa) {
      filtrados = filtrados.filter(v => v.empresa === filtroEmpresa);
    }
    
    // Filtro por delegación
    if (filtroDelegacion) {
      filtrados = filtrados.filter(v => v.delegacionCalculada === filtroDelegacion);
    }
    
    // Filtro por búsqueda de texto (case-insensitive y normalizado)
    if (busquedaVigilante.trim()) {
      filtrados = filtrados.filter(vigilante => {
        // Normalizar: quitar espacios extras, minúsculas, quitar acentos
        const normalizar = (texto) => {
          return texto
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
            .replace(/\s+/g, ' ') // Normalizar espacios múltiples
            .trim();
        };
        
        const nombreCompleto = normalizar(`${vigilante.nombre || ''} ${vigilante.apellidos || ''}`);
        const persona = normalizar(vigilante.persona || '');
        const nif = normalizar(vigilante.nif || '');
        const id = normalizar(vigilante.id || '');
        const busqueda = normalizar(busquedaVigilante);
        
        return nombreCompleto.includes(busqueda) || 
               persona.includes(busqueda) ||
               nif.includes(busqueda) ||
               id.includes(busqueda);
      });
    }
    
    setVigilantesFiltrados(filtrados);
  }, [busquedaVigilante, filtroEmpresa, filtroDelegacion, vigilantes, soloVigilantesCompletos]);

  const handleAsignarVigilante = async () => {
    if (!vigilanteSeleccionado) {
      setError('Debe seleccionar un usuario');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Buscar el usuario seleccionado
      const vigilante = vigilantesFiltrados.find(v => v.id === vigilanteSeleccionado) || 
                       vigilantesExternos.find(v => v.id === vigilanteSeleccionado);

      if (!vigilante) {
        throw new Error('Usuario no encontrado');
      }

      // Verificar que tiene código de persona válido
      if (!vigilante.persona || vigilante.persona === 'undefined' || vigilante.persona === 'null') {
        throw new Error('El usuario seleccionado no tiene un código de persona válido');
      }

      // 🆕 NUEVO FLUJO: Agregar a cambios pendientes en lugar de guardar inmediatamente
      if (onAsignacionPendiente) {
        console.log('📝 Agregando asignación a cambios pendientes...');
        
        // Crear el cambio pendiente
        const cambioPendiente = {
          turnoId: turnoId,
          tipo: 'asignacion',
          vigilanteAnterior: null, // Se llenará en GridView con el valor actual
          vigilanteNuevo: {
            id: vigilanteSeleccionado,
            persona: vigilante.persona,
            nombre: vigilante.nombre || '',
            apellidos: vigilante.apellidos || '',
            esExterno: vigilante.esExterno || false,
            servicioPrincipal: vigilante.esExterno ? vigilante.servicioPrincipal || null : null
          },
          timestamp: new Date().toISOString()
        };
        
        // Llamar al callback para agregar a cambios pendientes
        onAsignacionPendiente(cambioPendiente);
        
        console.log('✅ Asignación agregada a cambios pendientes');
      } else {
        // FALLBACK: Flujo antiguo si no hay callback de pendientes
        console.log('⚠️ No hay callback de pendientes, usando flujo antiguo (guardar inmediatamente)');
        
        // Si tenemos la función de Freematica, usarla
        if (onAsignarAFreematica) {
          setAsignandoAFreematica(true);
          try {
            const resultado = await onAsignarAFreematica(turnoId, vigilante.persona, vigilante);
          } catch (freematicaError) {
            console.error('Error asignando a Freematica:', freematicaError);
            setError(`Asignado localmente, pero error en Freematica: ${freematicaError.message}`);
          } finally {
            setAsignandoAFreematica(false);
          }
        } else {
          // Guardar directamente en Firestore
          const turnoRef = doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId, 'turnos', turnoId);
          
          const updateData = {
            vigilante: vigilante.persona,
            vigilanteId: vigilanteSeleccionado,
            sinAsignar: false,
            fechaAsignacion: new Date().toISOString(),
            esVigilanteExterno: vigilante.esExterno || false,
            servicioPrincipal: vigilante.esExterno ? vigilante.servicioPrincipal || null : null,
            personaId: vigilante.persona,
            nombrePersona: vigilante.nombre || '',
            apellido1: vigilante.apellidos || '',
            apellido2: '',
            fechaActualizacion: new Date().toISOString()
          };

          await updateDoc(turnoRef, updateData);
        }
        
        // Llamar al callback para actualizar la UI
        if (onVigilanteAsignado) {
          await onVigilanteAsignado();
        }
      }

      onClose();
    } catch (error) {
      console.error('Error al asignar usuario:', error);
      setError('Error al asignar usuario: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Limpiar búsqueda y filtros cuando se cierre el modal
  const handleClose = () => {
    setBusquedaVigilante('');
    setVigilanteSeleccionado('');
    setFiltroEmpresa('');
    setFiltroDelegacion('');
    setSoloVigilantesCompletos(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Asignar Usuario al Turno
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography ml={2}>Cargando usuarios...</Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Seleccione el usuario que desea asignar a este turno:
            </Typography>

            {/* Indicador de filtros activos */}
            {(filtroEmpresa || filtroDelegacion) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Filtros activos: 
                {filtroEmpresa && ` Empresa: ${filtroEmpresa}`}
                {filtroDelegacion && ` Delegación: ${filtroDelegacion}`}
              </Alert>
            )}

            {/* Controles de filtro */}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  🔍 Filtros
                </Typography>
                <Button 
                  size="small" 
                  onClick={() => {
                    setFiltroEmpresa('');
                    setFiltroDelegacion('');
                    setSoloVigilantesCompletos(false);
                  }}
                >
                  Limpiar Filtros
                </Button>
              </Box>
              
              {/* Checkbox para filtrar solo usuarios completos */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={soloVigilantesCompletos}
                    onChange={(e) => setSoloVigilantesCompletos(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Mostrar solo usuarios con empresa y delegación
                  </Typography>
                }
                sx={{ mb: 1 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    value={filtroEmpresa}
                    onChange={(e) => setFiltroEmpresa(e.target.value)}
                    label="Empresa"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="0001">0001</MenuItem>
                    <MenuItem value="0005">0005</MenuItem>
                    <MenuItem value="0006">0006</MenuItem>
                    <MenuItem value="0008">0008</MenuItem>
                    <MenuItem value="0013">0013</MenuItem>
                    <MenuItem value="0016">0016</MenuItem>
                    <MenuItem value="0020">0020</MenuItem>
                    <MenuItem value="0040">0040</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth size="small">
                  <InputLabel>Delegación</InputLabel>
                  <Select
                    value={filtroDelegacion}
                    onChange={(e) => setFiltroDelegacion(e.target.value)}
                    label="Delegación"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="01">01 - Álava</MenuItem>
                    <MenuItem value="02">02 - Albacete</MenuItem>
                    <MenuItem value="03">03 - Alicante</MenuItem>
                    <MenuItem value="04">04 - Almería</MenuItem>
                    <MenuItem value="05">05 - Ávila</MenuItem>
                    <MenuItem value="06">06 - Badajoz</MenuItem>
                    <MenuItem value="07">07 - Baleares</MenuItem>
                    <MenuItem value="08">08 - Barcelona</MenuItem>
                    <MenuItem value="09">09 - Burgos</MenuItem>
                    <MenuItem value="10">10 - Cáceres</MenuItem>
                    <MenuItem value="11">11 - Cádiz</MenuItem>
                    <MenuItem value="12">12 - Castellón</MenuItem>
                    <MenuItem value="13">13 - Ciudad Real</MenuItem>
                    <MenuItem value="14">14 - Córdoba</MenuItem>
                    <MenuItem value="15">15 - A Coruña</MenuItem>
                    <MenuItem value="16">16 - Cuenca</MenuItem>
                    <MenuItem value="17">17 - Girona</MenuItem>
                    <MenuItem value="18">18 - Granada</MenuItem>
                    <MenuItem value="19">19 - Guadalajara</MenuItem>
                    <MenuItem value="20">20 - Guipúzcoa</MenuItem>
                    <MenuItem value="21">21 - Huelva</MenuItem>
                    <MenuItem value="22">22 - Huesca</MenuItem>
                    <MenuItem value="23">23 - Jaén</MenuItem>
                    <MenuItem value="24">24 - León</MenuItem>
                    <MenuItem value="25">25 - Lleida</MenuItem>
                    <MenuItem value="26">26 - La Rioja</MenuItem>
                    <MenuItem value="27">27 - Lugo</MenuItem>
                    <MenuItem value="28">28 - Madrid</MenuItem>
                    <MenuItem value="29">29 - Málaga</MenuItem>
                    <MenuItem value="30">30 - Murcia</MenuItem>
                    <MenuItem value="31">31 - Navarra</MenuItem>
                    <MenuItem value="32">32 - Ourense</MenuItem>
                    <MenuItem value="33">33 - Asturias</MenuItem>
                    <MenuItem value="34">34 - Palencia</MenuItem>
                    <MenuItem value="35">35 - Las Palmas</MenuItem>
                    <MenuItem value="36">36 - Pontevedra</MenuItem>
                    <MenuItem value="37">37 - Salamanca</MenuItem>
                    <MenuItem value="38">38 - S.C. Tenerife</MenuItem>
                    <MenuItem value="39">39 - Cantabria</MenuItem>
                    <MenuItem value="40">40 - Segovia</MenuItem>
                    <MenuItem value="41">41 - Sevilla</MenuItem>
                    <MenuItem value="42">42 - Soria</MenuItem>
                    <MenuItem value="43">43 - Tarragona</MenuItem>
                    <MenuItem value="44">44 - Teruel</MenuItem>
                    <MenuItem value="45">45 - Toledo</MenuItem>
                    <MenuItem value="46">46 - Valencia</MenuItem>
                    <MenuItem value="47">47 - Valladolid</MenuItem>
                    <MenuItem value="48">48 - Vizcaya</MenuItem>
                    <MenuItem value="49">49 - Zamora</MenuItem>
                    <MenuItem value="50">50 - Zaragoza</MenuItem>
                    <MenuItem value="51">51 - Ceuta</MenuItem>
                    <MenuItem value="52">52 - Melilla</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Campo de búsqueda */}
            <TextField
              fullWidth
              label="Buscar usuario"
              placeholder="Escriba nombre, apellido o código de persona..."
              value={busquedaVigilante}
              onChange={(e) => setBusquedaVigilante(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />

            {/* Select de usuario */}
            <FormControl fullWidth>
              <InputLabel>Usuario</InputLabel>
              <Select
                value={vigilanteSeleccionado}
                onChange={(e) => setVigilanteSeleccionado(e.target.value)}
                label="Usuario"
              >
                {vigilantesFiltrados.map((vigilante) => {
                  const sinDatos = !vigilante.empresa || !vigilante.delegacionCalculada;
                  return (
                    <MenuItem 
                      key={vigilante.id} 
                      value={vigilante.id}
                      sx={{ 
                        backgroundColor: sinDatos ? '#fff3e0' : 'inherit',
                        '&:hover': {
                          backgroundColor: sinDatos ? '#ffe0b2' : undefined
                        }
                      }}
                    >
                      {formatearVigilante(vigilante)}
                      {vigilante.delegacionCalculada 
                        ? ` (Del: ${vigilante.delegacionCalculada})` 
                        : ' ⚠️ Sin delegación'}
                      {(!vigilante.empresa || vigilante.empresa === '') && ' ⚠️ Sin empresa'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Mensajes informativos */}
            <Typography variant="body2" color="text.secondary" mt={2}>
              {vigilantesFiltrados.length} usuario(s) disponible(s)
              {(filtroEmpresa || filtroDelegacion) && ' (filtrados)'}
            </Typography>

            {/* Advertencia de usuarios sin datos completos */}
            {(() => {
              const sinEmpresa = vigilantes.filter(v => !v.empresa || v.empresa === '').length;
              const sinDelegacion = vigilantes.filter(v => !v.delegacionCalculada).length;
              
              if (sinEmpresa > 0 || sinDelegacion > 0) {
                return (
                  <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    <Typography variant="caption" display="block">
                      ℹ️ Usuarios sin datos completos (pueden asignarse):
                    </Typography>
                    {sinEmpresa > 0 && (
                      <Typography variant="caption" display="block">
                        • {sinEmpresa} sin empresa definida
                      </Typography>
                    )}
                    {sinDelegacion > 0 && (
                      <Typography variant="caption" display="block">
                        • {sinDelegacion} sin delegación (código postal no válido)
                      </Typography>
                    )}
                  </Alert>
                );
              }
              return null;
            })()}

            {vigilantesFiltrados.length === 0 && (busquedaVigilante || filtroEmpresa || filtroDelegacion) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No se encontraron usuarios que coincidan con los filtros aplicados.
                <br />
                <Button size="small" onClick={() => {
                  setBusquedaVigilante('');
                  setFiltroEmpresa('');
                  setFiltroDelegacion('');
                }}>
                  Limpiar todos los filtros
                </Button>
              </Alert>
            )}

            {vigilantes.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No hay usuarios disponibles para asignar
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          onClick={handleAsignarVigilante} 
          variant="contained" 
          disabled={loading || asignandoAFreematica || !vigilanteSeleccionado}
        >
          {asignandoAFreematica ? 'Sincronizando con Freematica...' : loading ? 'Asignando...' : 'Asignar Usuario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AsignarVigilante; 